<template>
    <div class="max-w-8xl mx-auto w-full space-y-4">
        <!-- Page Header -->
        <div class="flex flex-wrap items-center justify-between gap-3">
            <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                Attendance Management
            </h1>
            <router-link to="/attendance/create" class="btn btn-primary">
                <Plus class="w-4 h-4 mr-1" />
                Add Attendance
            </router-link>
        </div>

        <!-- Search and Filters -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div class="space-y-3">
                <div class="flex flex-wrap gap-3">
                    <!-- Class Subject Filter -->
                    <SearchableSelect
                        v-model="filters.class_subject_id"
                        :options="classSubjectOptions"
                        placeholder="All Class Subjects"
                        :option-label="getClassSubjectLabel"
                        option-value="id"
                        class="flex-1 min-w-[180px]"
                        @update:model-value="fetchAttendances"
                    />

                    <!-- Mode Filter -->
                    <SearchableSelect
                        v-model="filters.mode"
                        :options="modeOptions"
                        placeholder="All Modes"
                        option-label="label"
                        option-value="value"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchAttendances"
                    />

                    <!-- Date Range -->
                    <DateRangePicker
                        v-model="dateRange"
                        class="flex-1 min-w-[200px]"
                    />

                    <button
                        @click="resetFilters()"
                        v-if="hasActiveFilters"
                        class="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                    >
                        <RotateCcw class="w-4 h-4" />
                        Reset Filters
                    </button>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div
            v-if="loading"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <SkeletonLoader variant="table" :rows="6" :cols="7" />
        </div>

        <!-- Error State -->
        <div
            v-if="error"
            class="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4"
        >
            <div class="flex">
                <div class="flex-shrink-0">
                    <div class="h-5 w-5 text-danger-400">⚠️</div>
                </div>
                <div class="ml-3">
                    <h3
                        class="text-sm font-medium text-danger-800 dark:text-danger-200"
                    >
                        Error
                    </h3>
                    <p
                        class="mt-1 text-sm text-danger-700 dark:text-danger-300"
                    >
                        {{ error }}
                    </p>
                    <button
                        @click="clearError"
                        class="mt-2 text-sm text-danger-600 dark:text-danger-400 hover:text-danger-500"
                    >
                        Dismiss
                    </button>
                </div>
            </div>
        </div>

        <!-- Data Table -->
        <div
            v-if="!loading && !error"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <div class="overflow-x-auto">
                <table
                    class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                    style="width: 100%"
                >
                    <thead class="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Date
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Class Subject
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Teacher
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Mode
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Time
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Attendance
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Status
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody
                        class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                    >
                        <tr
                            v-for="attendance in attendances"
                            :key="attendance.id"
                            class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ formatDate(attendance.attendance_date) }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{
                                    attendance.class_subject?.class?.name || "-"
                                }}
                                -
                                {{
                                    attendance.class_subject?.subject?.name ||
                                    "-"
                                }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ attendance.teacher?.user?.name || "-" }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap">
                                <span
                                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                                    :class="
                                        attendance.mode === 'qr'
                                            ? 'bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200'
                                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                                    "
                                >
                                    {{ attendance.mode_text }}
                                </span>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ formatTime(attendance.start_time) }} –
                                {{ formatTime(attendance.end_time) }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ attendance.present_count || 0 }}/{{
                                    attendance.total_count || 0
                                }}
                                ({{ attendance.attendance_percentage ?? 0 }}%)
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap">
                                <span
                                    v-if="attendance.is_locked"
                                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                                >
                                    Locked
                                </span>
                                <span
                                    v-else-if="isOngoing(attendance)"
                                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200"
                                >
                                    Ongoing
                                </span>
                                <span
                                    v-else
                                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-warning-100 text-warning-800 dark:bg-warning-900 dark:text-warning-200"
                                >
                                    Ended
                                </span>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm font-medium"
                            >
                                <button
                                    @click="viewAttendance(attendance)"
                                    title="Manage Attendance"
                                    class="inline-flex items-center justify-center p-2 text-success-600 dark:text-success-400 hover:text-success-900 dark:hover:text-success-300 rounded-md hover:bg-success-50 dark:hover:bg-success-900/20 transition-colors mr-2"
                                >
                                    <Eye class="w-4 h-4" />
                                </button>
                                <button
                                    @click="viewRecap(attendance)"
                                    title="View Recap"
                                    class="inline-flex items-center justify-center p-2 text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 rounded-md hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors mr-2"
                                >
                                    <BarChart3 class="w-4 h-4" />
                                </button>
                                <button
                                    @click="confirmDelete(attendance)"
                                    title="Delete Attendance"
                                    class="inline-flex items-center justify-center p-2 text-danger-600 dark:text-danger-400 hover:text-danger-900 dark:hover:text-danger-300 rounded-md hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
                                >
                                    <Trash2 class="w-4 h-4" />
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Empty State -->
            <EmptyState
                v-if="!loading && attendances.length === 0"
                icon="CalendarIcon"
                title="No attendance sessions found"
                description="Create a new attendance session to start tracking student attendance."
            />

            <!-- Pagination -->
            <BasePagination
                v-if="attendances.length > 0"
                :pagination="meta"
                :per-page="filters.per_page || 15"
                :per-page-options="[10, 15, 25, 50, 100]"
                @page-change="goToPage"
                @per-page-change="handlePerPageChange"
            />
        </div>

        <DeleteConfirmationModal
            :show="showDeleteModal"
            title="Delete Attendance Session"
            message="Are you sure you want to delete this attendance session? This action cannot be undone."
            @confirm="deleteAttendance"
            @close="closeDeleteModal"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import SearchableSelect from "@/components/SearchableSelect.vue";
import DateRangePicker, {
    type DateRangeValue,
} from "@/components/DateRangePicker.vue";
import BasePagination from "@/components/BasePagination.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import EmptyState from "@/components/EmptyState.vue";
import DeleteConfirmationModal from "@/components/DeleteConfirmationModal.vue";
import { RotateCcw, Eye, Trash2, Plus, BarChart3 } from "@lucide/vue";
import attendanceApi, { Attendance, AttendanceFilters } from "@/api/attendance";
import { classSubjectApi } from "@/api/classSubject";

const router = useRouter();
const attendances = ref<Attendance[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);
const meta = ref<any>(null);

const filters = ref<AttendanceFilters>({
    class_subject_id: null,
    class_id: null,
    mode: null,
    teacher_id: null,
    date_from: null,
    date_to: null,
    per_page: 15,
});

const classSubjectOptions = ref<any[]>([]);

const dateRange = computed<DateRangeValue>({
    get: () => ({
        date_from: filters.value.date_from ?? null,
        date_to: filters.value.date_to ?? null,
    }),
    set: (value) => {
        filters.value.date_from = value?.date_from ?? null;
        filters.value.date_to = value?.date_to ?? null;
        filters.value.page = 1;
        fetchAttendances();
    },
});

const modeOptions = [
    { label: "QR Code", value: "qr" },
    { label: "Manual", value: "manual" },
];

const hasActiveFilters = computed(() => {
    const { class_subject_id, mode, date_from, date_to } = filters.value;
    return (
        (class_subject_id !== null && class_subject_id !== undefined) ||
        (mode !== null && mode !== undefined) ||
        (date_from !== null && date_from !== undefined && date_from !== "") ||
        (date_to !== null && date_to !== undefined && date_to !== "")
    );
});

async function fetchAttendances() {
    loading.value = true;
    error.value = null;

    try {
        const response = await attendanceApi.getAll({
            ...filters.value,
            date_from: filters.value.date_from || undefined,
            date_to: filters.value.date_to || undefined,
        });

        attendances.value = response.data.items;
        meta.value = response.data.meta;
    } catch (err: any) {
        error.value =
            err.response?.data?.message || "Failed to fetch attendances";
    } finally {
        loading.value = false;
    }
}

function resetFilters() {
    filters.value = {
        class_subject_id: null,
        class_id: null,
        mode: null,
        teacher_id: null,
        date_from: null,
        date_to: null,
        per_page: 15,
    };
    fetchAttendances();
}

function clearError() {
    error.value = null;
}

async function fetchClassSubjects() {
    try {
        const response = await classSubjectApi.getAll({ per_page: 1000 });
        classSubjectOptions.value = response.data.items || [];
    } catch {
        classSubjectOptions.value = [];
    }
}

function getClassSubjectLabel(option: any) {
    return `${option.class?.grade_level || ""} - ${option.class?.name || ""} - ${option.subject?.name || ""}`;
}

function formatDate(dateString: string) {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
}

function formatTime(dateString: string) {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
    });
}

function isOngoing(attendance: Attendance) {
    const now = new Date();
    const start = new Date(attendance.start_time);
    const end = new Date(attendance.end_time);
    return now >= start && now <= end && !attendance.is_locked;
}

const attendanceToDelete = ref<Attendance | null>(null);

function viewAttendance(attendance: Attendance) {
    router.push(`/attendance/${attendance.id}`);
}

function viewRecap(attendance: Attendance) {
    router.push(
        `/attendance/recap?class_subject_id=${attendance.class_subject_id}`,
    );
}

const showDeleteModal = ref(false);

function confirmDelete(attendance: Attendance) {
    attendanceToDelete.value = attendance;
    showDeleteModal.value = true;
}

function closeDeleteModal() {
    showDeleteModal.value = false;
    attendanceToDelete.value = null;
}

async function deleteAttendance() {
    if (!attendanceToDelete.value) return;

    try {
        await attendanceApi.delete(attendanceToDelete.value.id);
        await fetchAttendances();
    } catch (err: any) {
        error.value =
            err.response?.data?.message || "Failed to delete attendance";
    } finally {
        closeDeleteModal();
    }
}

function goToPage(page: number) {
    if (meta.value) {
        filters.value.page = page;
        fetchAttendances();
    }
}

function handlePerPageChange(value: number) {
    filters.value.per_page = value;
    filters.value.page = 1;
    fetchAttendances();
}

onMounted(() => {
    fetchAttendances();
    fetchClassSubjects();
});
</script>
